export enum LoanType {
  Loan = 1,
  SalaryAdvance = 2,
}

export const LoanTypeLabels: Record<LoanType, string> = {
  [LoanType.Loan]: 'Loan',
  [LoanType.SalaryAdvance]: 'Salary Advance',
};

export enum LoanInterestMethod {
  None = 0,
  FlatRate = 1,
  ReducingBalance = 2,
}

export const LoanInterestMethodLabels: Record<LoanInterestMethod, string> = {
  [LoanInterestMethod.None]: 'No Interest',
  [LoanInterestMethod.FlatRate]: 'Flat Rate',
  [LoanInterestMethod.ReducingBalance]: 'Reducing Balance',
};

export enum LoanStatus {
  Pending = 1,
  Approved = 2,
  Rejected = 3,
  Active = 4,
  Closed = 5,
  Cancelled = 6,
  WrittenOff = 7,
  Foreclosed = 8,
}

export const LoanStatusLabels: Record<LoanStatus, string> = {
  [LoanStatus.Pending]: 'Pending Approval',
  [LoanStatus.Approved]: 'Approved',
  [LoanStatus.Rejected]: 'Rejected',
  [LoanStatus.Active]: 'Active',
  [LoanStatus.Closed]: 'Closed',
  [LoanStatus.Cancelled]: 'Cancelled',
  [LoanStatus.WrittenOff]: 'Written Off',
  [LoanStatus.Foreclosed]: 'Foreclosed',
};

export const LoanStatusColors: Record<LoanStatus, 'default' | 'info' | 'success' | 'warning' | 'error'> = {
  [LoanStatus.Pending]: 'warning',
  [LoanStatus.Approved]: 'info',
  [LoanStatus.Rejected]: 'error',
  [LoanStatus.Active]: 'success',
  [LoanStatus.Closed]: 'default',
  [LoanStatus.Cancelled]: 'default',
  [LoanStatus.WrittenOff]: 'error',
  [LoanStatus.Foreclosed]: 'default',
};

/**
 * A request in one of these states was never disbursed, so it carries no liability - the money
 * never left the company. Financial columns are blanked for these rows so a dead request is never
 * mistaken for one that still needs recovery.
 */
export const isNeverDisbursed = (status: LoanStatus): boolean =>
  status === LoanStatus.Rejected || status === LoanStatus.Cancelled;

export enum LoanInstallmentStatus {
  Pending = 1,
  Recovered = 2,
  PartiallyRecovered = 3,
  Waived = 4,
  Skipped = 5,
}

export const LoanInstallmentStatusLabels: Record<LoanInstallmentStatus, string> = {
  [LoanInstallmentStatus.Pending]: 'Pending',
  [LoanInstallmentStatus.Recovered]: 'Recovered',
  [LoanInstallmentStatus.PartiallyRecovered]: 'Partial',
  [LoanInstallmentStatus.Waived]: 'Waived',
  [LoanInstallmentStatus.Skipped]: 'Skipped',
};

export enum LoanTransactionType {
  Disbursement = 1,
  PayrollRecovery = 2,
  ManualRepayment = 3,
  WriteOff = 4,
  Foreclosure = 5,
  Reversal = 6,
  Skip = 7,
}

export const LoanTransactionTypeLabels: Record<LoanTransactionType, string> = {
  [LoanTransactionType.Disbursement]: 'Disbursement',
  [LoanTransactionType.PayrollRecovery]: 'Payroll Recovery',
  [LoanTransactionType.ManualRepayment]: 'Manual Repayment',
  [LoanTransactionType.WriteOff]: 'Write Off / Waiver',
  [LoanTransactionType.Foreclosure]: 'Foreclosure',
  [LoanTransactionType.Reversal]: 'Reversal',
  [LoanTransactionType.Skip]: 'Installment Skipped',
};

export interface LoanListItemDto {
  id: string;
  referenceNumber: string;
  employeeId: string;
  employeeName: string;
  employeeCode: string;
  departmentName?: string | null;
  loanType: LoanType;
  principalAmount: number;
  totalPayable: number;
  outstandingBalance: number;
  totalPaid: number;
  totalInstallments: number;
  installmentsPaid: number;
  installmentAmount: number;
  status: LoanStatus;
  repaymentStartMonth: number;
  repaymentStartYear: number;
  createdAt: string;
  disbursedOn?: string | null;
}

export interface LoanInstallmentDto {
  id: string;
  installmentNumber: number;
  dueMonth: number;
  dueYear: number;
  dueDate: string;
  principalComponent: number;
  interestComponent: number;
  amount: number;
  paidAmount: number;
  status: LoanInstallmentStatus;
  recoveredOn?: string | null;
  payrollRunId?: string | null;
  remarks?: string | null;
}

export interface LoanTransactionDto {
  id: string;
  transactionType: LoanTransactionType;
  transactionDate: string;
  amount: number;
  principalPortion: number;
  interestPortion: number;
  balanceAfter: number;
  payrollRunId?: string | null;
  reference?: string | null;
  remarks?: string | null;
  createdBy?: string | null;
}

export interface LoanDetailDto extends LoanListItemDto {
  interestMethod: LoanInterestMethod;
  interestRatePercent: number;
  totalInterest: number;
  outstandingPrincipal: number;
  purpose?: string | null;
  disbursedAmount?: number | null;
  disbursementReference?: string | null;
  requestedBy?: string | null;
  requestedOn?: string | null;
  approvedBy?: string | null;
  approvedOn?: string | null;
  approvalRemarks?: string | null;
  rejectedBy?: string | null;
  rejectedOn?: string | null;
  rejectionReason?: string | null;
  closedOn?: string | null;
  closureRemarks?: string | null;
  approvalRequestId?: string | null;
  installments: LoanInstallmentDto[];
  transactions: LoanTransactionDto[];
}

export interface LoanSchedulePreviewDto {
  totalInterest: number;
  totalPayable: number;
  installmentAmount: number;
  installments: LoanInstallmentDto[];
}

export interface LoanSummaryDto {
  activeLoans: number;
  pendingRequests: number;
  totalOutstanding: number;
  totalDisbursedThisYear: number;
  expectedRecoveryThisMonth: number;
}

export interface LoanEmployeeOptionDto {
  id: string;
  name: string;
  employeeCode: string;
  departmentName?: string | null;
  hasOpenLoan: boolean;
}

export interface CreateLoanRequestPayload {
  // Omitted for employee self-service submissions (POST /loans/my) - the server always
  // resolves the requester's own employee id there and ignores whatever is sent.
  employeeId?: string;
  loanType: LoanType;
  principalAmount: number;
  interestMethod: LoanInterestMethod;
  interestRatePercent: number;
  totalInstallments: number;
  repaymentStartMonth: number;
  repaymentStartYear: number;
  purpose?: string;
  autoApprove?: boolean;
}
